/**
 * Upcoming milestones and record chases, for the Milestones page: the round
 * numbers active managers are closing in on, and the all-time records within
 * reach.
 *
 * Everything is counted the Hall of Fame's way: regular-season and playoff
 * games count, consolation games don't. Each kind has one interval
 * (`MILESTONE_STEPS`), shared with the weekly matchup facts, so a milestone is
 * celebrated at one size everywhere.
 *
 * A milestone is "coming up" when the manager's career rate says it should
 * arrive within the next few counted games, or when it's the very next one of
 * a count (the next win, the next 200-point game). The playoff kinds go by the
 * live season's playoff odds and calendar instead. Rivalry meetings aren't
 * milestones here (the matchup report still marks a pair's 10th, 20th, ...
 * meeting on its own).
 */

import type { LeagueHistory, SeasonHistory } from "../history";
import type { PlayoffOdds } from "../odds/playoffOdds";
import type { PersonGame, Record3 } from "../reportBuilder/ledger";
import { buildLedger, countsTowardHistory } from "../reportBuilder/ledger";
import { getAwardDefinition } from "../types";
import { round2 } from "../util/math";
import { incompleteWeeks, weeklyBlowouts } from "./hallOfFame";
import type { ManagerSeasonLine } from "./seasonLines";
import { buildSeasonLines } from "./seasonLines";
import type { Streak } from "./streaks";
import { compareStreaks, summarizeStreaks } from "./streaks";

/**
 * One interval per milestone kind, each a few seasons apart for a typical
 * manager, so every one is an occasion. The matchup report's milestone facts
 * use the same steps.
 */
export const MILESTONE_STEPS = {
  careerPoints: 5000,
  careerWins: 25,
  bigGames: 25,
  hugeGames: 5,
  playoffAppearances: 5,
  playoffWins: 5,
  blowoutAwards: 5,
} as const;

export type MilestoneKind = keyof typeof MILESTONE_STEPS;

/** A "150-point game" and a "200-point game". */
const BIG_GAME_POINTS = 150;
const HUGE_GAME_POINTS = 200;

/** How many counted games ahead a milestone, or passing a career leader, counts as coming up. */
export const MILESTONE_WINDOW_GAMES = 3;
/**
 * A rarer count (a 200-point game, a blowout award) is never "likely in three
 * games", so its very next one also counts as coming up when the career rate
 * puts it within about a regular season.
 */
export const NEXT_ONE_WINDOW_GAMES = 14;
/** A season pace within this share of a single-season record is a chase. */
export const RECORD_PACE_SHARE = 0.95;
/** Games into a season before its pace means anything. */
export const MIN_PACE_GAMES = 4;
/** An active win streak this close to the longest ever is a chase. */
export const STREAK_CHASE_GAP = 2;

/** The next multiple of `step` above `value` (a value on a multiple has reached it, so it's the one after). */
export function nextMilestone(value: number, step: number): number {
  return (Math.floor(value / step) + 1) * step;
}

export interface MilestoneEntry {
  personId: string;
  current: number;
  /** The next multiple of the kind's step. */
  target: number;
  /** How far from the previous milestone to `target`, 0 to 1. */
  progress: number;
  needed: number;
  /** Counted games until it's expected at the manager's career rate; null with no rate to go on. */
  gamesAway: number | null;
  /** Playoff kinds during the regular season: the chance of making the playoffs this season. */
  playoffOdds: number | null;
  /** The next chance to add to it is next season (the season's over, or this one can't deliver it any more). */
  nextSeason: boolean;
  /** Close on the numbers: expected within the window (the browse view's gold bar). */
  soon: boolean;
  /** Where it sits on the page's timeline; null when it isn't in sight. */
  when: MilestoneWhen | null;
}

/**
 * The page's timeline: riding on this week's game, expected in the next few
 * weeks, later this season, waiting on a playoff spot, or next season.
 */
export type MilestoneWhen = "thisWeek" | "nextFewWeeks" | "laterThisSeason" | "ifPlayoffs" | "nextSeason";

/** A milestone already reached: the game that got there. */
export interface ReachedMilestone {
  kind: MilestoneKind;
  personId: string;
  /** The round number reached, e.g. 75 (wins) or 20000 (points). */
  value: number;
  season: number;
  week: number;
  /**
   * Where this manager came in reaching the mark across the league's history:
   * 3 means the 3rd ever (managers who got there the same week share a place).
   * The rarer the mark, the lower the number, so it also ranks the list.
   */
  nth: number;
}

export interface MilestoneCategory {
  kind: MilestoneKind;
  step: number;
  /** Coming-up first, then closest first. */
  entries: MilestoneEntry[];
}

export type RecordChaseKind =
  | "seasonPointsPerGame"
  | "seasonWins"
  | "seasonBigGames"
  | "winStreak"
  | "careerWins"
  | "careerPoints"
  | "careerBigGames"
  | "careerHugeGames"
  | "careerBlowoutAwards";

export interface RecordChase {
  kind: RecordChaseKind;
  personId: string;
  /** The chaser's mark so far: points per game for that record, a count otherwise. */
  current: number;
  /** A season count's pace over the full regular season; null for the rest. */
  pace: number | null;
  /**
   * Season chases: the chaser's running mark after each regular-season game
   * (a running total, or a running average for points per game), and the
   * record season's, for a race chart. Null for the rest.
   */
  race: { chaser: number[]; record: number[]; seasonGames: number } | null;
  record: number;
  /** The most recent holder, when several share the mark (as on the Hall of Fame). */
  holderId: string;
  /** How many seasons, streaks or managers hold the record; more than 1 when it's shared. */
  recordHolders: number;
  /** When a single-season or streak record was set; null for a career total. */
  holderSeason: number | null;
  /** Season chases: the chaser's regular-season record so far; null for the rest. */
  seasonRecord: Record3 | null;
  /** Career totals: counted games until passing the leader, at the chaser's career rate. */
  gamesAway: number | null;
  /** What's already banked as a share of the record, capped at 1: `current` over the record. */
  banked: number;
  /** Where the chaser is headed as a share of the record, capped at 1: the pace for a season count, otherwise `banked`. */
  progress: number;
}

export interface Milestones {
  /** No season is live: every estimate looks ahead to next season. */
  offseason: boolean;
  /** The latest season, the one `reached` covers. */
  season: number | null;
  /** This week's opponent for each active manager with a game still to play. */
  opponents: Record<string, string>;
  /** Milestones reached in the latest season, most recent first. */
  reached: ReachedMilestone[];
  /** Every kind, in display order, including ones with no entries. */
  categories: MilestoneCategory[];
  /** Closest to the record first. */
  recordChases: RecordChase[];
}

/** One manager's career, the counts every milestone and career record reads. */
interface Tally {
  games: number;
  wins: number;
  points: number;
  bigGames: number;
  hugeGames: number;
  playoffGames: number;
  playoffWins: number;
  playoffSeasons: Set<number>;
  /** Games that could have won a blowout award (regular season, the award's weeks, finished weeks). */
  blowoutChances: number;
  blowoutAwards: number;
}

/** Where the live season stands, for what can still happen in it. */
interface SeasonState {
  live: SeasonHistory | null;
  /** The live season's regular season is still being played. */
  regularSeason: boolean;
  /** Regular-season weeks left before the playoffs, counting this one; null outside the regular season or when unknown. */
  weeksToPlayoffs: number | null;
  /** Managers with a counted game still to play this season. */
  playingOn: ReadonlySet<string>;
  /** This week's unfinished counted games: each manager's opponent. */
  opponents: ReadonlyMap<string, string>;
}

/** When a kind can grow, which decides whether "coming up" is this season or next. */
type Opportunity = "anyGame" | "blowoutWeeks" | "playoffGames" | "playoffBerth";

interface KindRule {
  kind: MilestoneKind;
  value: (t: Tally) => number;
  /** Games that could have added to it, for the career rate. */
  chances: (t: Tally) => number;
  /** A count, so the very next one is always worth showing; points are continuous. */
  countable: boolean;
  opportunity: Opportunity;
}

const KIND_RULES: readonly KindRule[] = [
  { kind: "careerPoints", value: (t) => t.points, chances: (t) => t.games, countable: false, opportunity: "anyGame" },
  { kind: "careerWins", value: (t) => t.wins, chances: (t) => t.games, countable: true, opportunity: "anyGame" },
  { kind: "bigGames", value: (t) => t.bigGames, chances: (t) => t.games, countable: true, opportunity: "anyGame" },
  { kind: "hugeGames", value: (t) => t.hugeGames, chances: (t) => t.games, countable: true, opportunity: "anyGame" },
  { kind: "playoffAppearances", value: (t) => t.playoffSeasons.size, chances: () => 0, countable: true, opportunity: "playoffBerth" },
  { kind: "playoffWins", value: (t) => t.playoffWins, chances: (t) => t.playoffGames, countable: true, opportunity: "playoffGames" },
  { kind: "blowoutAwards", value: (t) => t.blowoutAwards, chances: (t) => t.blowoutChances, countable: true, opportunity: "blowoutWeeks" },
];

const ruleFor = (kind: KindRule["kind"]): KindRule => KIND_RULES.find((r) => r.kind === kind)!;

/** The page's order: the headline career totals, then single-game feats, the playoffs, and rivalries. */
const DISPLAY_ORDER: readonly MilestoneKind[] = [
  "careerWins",
  "careerPoints",
  "hugeGames",
  "bigGames",
  "blowoutAwards",
  "playoffAppearances",
  "playoffWins",
];

/** Which career records a chase can be for, and the milestone rule that counts each. */
const CAREER_CHASES: readonly { kind: RecordChaseKind; rule: KindRule["kind"] }[] = [
  { kind: "careerWins", rule: "careerWins" },
  { kind: "careerPoints", rule: "careerPoints" },
  { kind: "careerBigGames", rule: "bigGames" },
  { kind: "careerHugeGames", rule: "hugeGames" },
  { kind: "careerBlowoutAwards", rule: "blowoutAwards" },
];

/**
 * Every active manager's next milestone of each kind, plus the records
 * within reach. `odds` is the live season's playoff odds (`simulatePlayoffOdds`),
 * passed in because the simulation is slow enough that callers cache it.
 */
export function buildMilestones(history: LeagueHistory, odds: PlayoffOdds | null = null): Milestones {
  const ledger = buildLedger(history);
  const games = ledger.filter(countsTowardHistory);
  const state = seasonState(history);
  const active = activeManagers(history);
  const { tallies, reached } = replayCareers(history, games, state, active);
  const oddsOf = (personId: string): number | null =>
    odds && state.live && odds.season === state.live.season ? (odds.teams.find((t) => t.personId === personId)?.playoffOdds ?? null) : null;

  const entriesOf = (kind: MilestoneKind): MilestoneEntry[] => {
    const rule = ruleFor(kind);
    return active.flatMap((id) => (tallies.has(id) ? [personEntry(rule, id, tallies.get(id)!, state, oddsOf(id))] : []));
  };

  return {
    offseason: state.live === null,
    season: history.seasons.at(-1)?.season ?? null,
    opponents: Object.fromEntries(state.opponents),
    reached,
    categories: DISPLAY_ORDER.map((kind) => ({ kind, step: MILESTONE_STEPS[kind], entries: sortEntries(kind, entriesOf(kind)) })),
    recordChases: [
      ...seasonChases(history, ledger, active, state),
      ...streakChases(history, games, active, state),
      ...careerChases(tallies, active, state),
    ].sort((a, b) => b.progress - a.progress || (a.gamesAway ?? 0) - (b.gamesAway ?? 0)),
  };
}

function seasonState(history: LeagueHistory): SeasonState {
  const latest = history.seasons.at(-1);
  if (!latest || latest.isFinished || latest.currentWeek === null) {
    return { live: null, regularSeason: false, weeksToPlayoffs: null, playingOn: new Set(), opponents: new Map() };
  }
  const week = latest.currentWeek;
  const playoffStart = latest.playoffStartWeek;
  const regularSeason = playoffStart === null || week < playoffStart;
  const personOf = (teamKey: string) => history.personByTeamKey[teamKey];
  // In the playoffs, only a team with a playoff game this week is still playing games that count.
  const teamKeys = regularSeason
    ? latest.teams.map((t) => t.teamKey)
    : latest.upcoming.filter((g) => g.gameType === "playoffs").flatMap((g) => g.teamKeys);
  const opponents = new Map<string, string>();
  for (const g of latest.upcoming) {
    const [a, b] = g.teamKeys.map(personOf);
    if (g.gameType === "consolation" || !a || !b) continue;
    opponents.set(a, b).set(b, a);
  }
  return {
    live: latest,
    regularSeason,
    weeksToPlayoffs: regularSeason && playoffStart !== null ? playoffStart - week : null,
    playingOn: new Set(teamKeys.map(personOf).filter((id): id is string => id !== undefined)),
    opponents,
  };
}

/** Managers with a team in the latest season, in the history's own order. */
function activeManagers(history: LeagueHistory): string[] {
  const ids = new Set((history.seasons.at(-1)?.teams ?? []).map((t) => history.personByTeamKey[t.teamKey]));
  return history.people.filter((p) => ids.has(p.id)).map((p) => p.id);
}

/**
 * Every manager's career tallies, replayed game by game, noting each round
 * number crossed in the latest season (by an active manager) along the way.
 */
function replayCareers(
  history: LeagueHistory,
  games: readonly PersonGame[],
  state: SeasonState,
  active: readonly string[],
): { tallies: Map<string, Tally>; reached: ReachedMilestone[] } {
  const tallies = new Map<string, Tally>();
  const tallyOf = (personId: string) => {
    let tally = tallies.get(personId);
    if (!tally) {
      tally = {
        games: 0,
        wins: 0,
        points: 0,
        bigGames: 0,
        hugeGames: 0,
        playoffGames: 0,
        playoffWins: 0,
        playoffSeasons: new Set(),
        blowoutChances: 0,
        blowoutAwards: 0,
      };
      tallies.set(personId, tally);
    }
    return tally;
  };

  const latest = history.seasons.at(-1)?.season;
  const isActive = new Set(active);
  const reached: ReachedMilestone[] = [];
  /** Every manager's crossing of every mark, by "kind|value", for each reached mark's place in line. */
  const crossings = new Map<string, { personId: string; season: number; week: number }[]>();

  const incomplete = incompleteWeeks(history);
  const awards = new Set(weeklyBlowouts(games, incomplete));
  const { firstWeek, lastWeek } = getAwardDefinition("biggestBlowout");

  for (const g of games) {
    const t = tallyOf(g.personId);
    const before = KIND_RULES.map((rule) => rule.value(t));
    t.games++;
    t.points += g.points;
    if (g.result === "W") t.wins++;
    if (g.points >= BIG_GAME_POINTS) t.bigGames++;
    if (g.points >= HUGE_GAME_POINTS) t.hugeGames++;
    if (g.gameType === "playoffs") {
      t.playoffGames++;
      if (g.result === "W") t.playoffWins++;
      t.playoffSeasons.add(g.season);
    }
    const awardWeek = g.gameType === "regular" && g.week >= firstWeek && g.week <= lastWeek;
    if (awardWeek && !incomplete.has(`${g.season}-${g.week}`)) t.blowoutChances++;
    if (awards.has(g)) t.blowoutAwards++;

    // No game adds a whole step (5,000 points, or 5 of a count), so a game crosses at most one mark per kind.
    KIND_RULES.forEach((rule, i) => {
      const step = MILESTONE_STEPS[rule.kind];
      const mark = Math.floor(rule.value(t) / step) * step;
      if (mark <= before[i]!) return;
      const key = `${rule.kind}|${mark}`;
      crossings.set(key, [...(crossings.get(key) ?? []), { personId: g.personId, season: g.season, week: g.week }]);
      if (g.season === latest && isActive.has(g.personId)) {
        reached.push({ kind: rule.kind, personId: g.personId, value: mark, season: g.season, week: g.week, nth: 0 });
      }
    });
  }
  // A team in this week's playoff games has made the field, even before its first playoff game is final.
  if (state.live && !state.regularSeason) for (const id of state.playingOn) tallyOf(id).playoffSeasons.add(state.live.season);
  for (const t of tallies.values()) t.points = round2(t.points);
  // One more than the managers, retired ones included, who got there in an earlier week.
  for (const r of reached) {
    const earlier = (crossings.get(`${r.kind}|${r.value}`) ?? []).filter((c) => c.season < r.season || (c.season === r.season && c.week < r.week));
    r.nth = earlier.length + 1;
  }
  return { tallies, reached: reached.sort(byImportance) };
}

/**
 * Most important first: the earlier in league history the manager got there
 * (the rarer the mark), the higher it ranks, so a 3rd-ever 75th 150-point game
 * outranks an 8th-ever 20,000th point. Equal places go most recent first.
 */
function byImportance(a: ReachedMilestone, b: ReachedMilestone): number {
  return a.nth - b.nth || b.season - a.season || b.week - a.week;
}

/** The live season still has a week that can hand out a blowout award. */
function inBlowoutWeeks(state: SeasonState): boolean {
  return state.regularSeason && state.live!.currentWeek! <= getAwardDefinition("biggestBlowout").lastWeek;
}

/** Counted games to add `needed` at a career rate of `hits` per `chances`; null with no rate to go on. */
function gamesAt(needed: number, hits: number, chances: number): number | null {
  if (chances === 0 || hits === 0) return null;
  return Math.round((needed / (hits / chances)) * 10) / 10;
}

function personEntry(rule: KindRule, personId: string, tally: Tally, state: SeasonState, playoffOdds: number | null): MilestoneEntry {
  const step = MILESTONE_STEPS[rule.kind];
  const current = rule.value(tally);
  const target = nextMilestone(current, step);
  const needed = round2(target - current);
  const gamesAway = gamesAt(needed, rule.value(tally), rule.chances(tally));
  // Close on the numbers: expected within the window, or the very next one of a count that should come this season.
  const close =
    gamesAway !== null && (gamesAway <= MILESTONE_WINDOW_GAMES || (rule.countable && needed === 1 && gamesAway <= NEXT_ONE_WINDOW_GAMES));
  // One game could do it: the next one of a count, or points a typical game of theirs would cover.
  const oneGame = rule.countable ? needed === 1 : gamesAway !== null && gamesAway <= 1;
  const base = { personId, current, target, progress: 1 - needed / step, needed, gamesAway };
  const playing = state.playingOn.has(personId);
  const thisWeek = state.opponents.has(personId);
  /** Where a close milestone that any counted game (or any game in reach) could deliver sits on the timeline. */
  const timeline = (canHappenNow: boolean): MilestoneWhen | null => {
    if (!close) return null;
    if (!canHappenNow) return state.live ? null : "nextSeason";
    if (thisWeek && oneGame) return "thisWeek";
    return gamesAway !== null && gamesAway <= MILESTONE_WINDOW_GAMES ? "nextFewWeeks" : "laterThisSeason";
  };

  switch (rule.opportunity) {
    case "anyGame":
      return { ...base, playoffOdds: null, nextSeason: !playing, soon: close, when: timeline(playing) };
    case "blowoutWeeks": {
      const thisSeason = playing && inBlowoutWeeks(state);
      return { ...base, playoffOdds: null, nextSeason: !thisSeason, soon: close && (thisSeason || !state.live), when: timeline(thisSeason) };
    }
    case "playoffGames": {
      // Only in a live season, and only once a playoff game is within the window: before then it's months off.
      if (!state.live) return { ...base, playoffOdds: null, nextSeason: true, soon: false, when: null };
      if (state.regularSeason) {
        const reachable = playoffOdds !== 0 && state.weeksToPlayoffs !== null && state.weeksToPlayoffs < MILESTONE_WINDOW_GAMES;
        const soon = close && reachable;
        return { ...base, playoffOdds, nextSeason: playoffOdds === 0, soon, when: soon ? "ifPlayoffs" : null };
      }
      return { ...base, playoffOdds: null, nextSeason: !playing, soon: close && playing, when: timeline(playing) };
    }
    case "playoffBerth": {
      // One a season, settled when the field is: the live regular season decides it, with odds to show for it.
      if (!state.live || !state.regularSeason) return { ...base, gamesAway: null, playoffOdds: null, nextSeason: true, soon: false, when: null };
      const soon = needed === 1 && playoffOdds !== 0;
      return { ...base, gamesAway: null, playoffOdds, nextSeason: playoffOdds === 0, soon, when: soon ? "ifPlayoffs" : null };
    }
  }
}

/**
 * Coming-up first, and among those the bigger milestone leads (a 25,000th
 * point before a 15,000th, however close each is). Then closest first: by the
 * estimate for points (the only kind that shows one), by how many are still
 * needed for a count, then by playoff odds for the playoff kinds (a playoff
 * win needs a playoff spot first), with the estimate breaking what's left so
 * the likelier of two "next one"s leads.
 */
function sortEntries(kind: MilestoneKind, entries: MilestoneEntry[]): MilestoneEntry[] {
  const byNeeded = kind !== "careerPoints";
  return entries.sort(
    (x, y) =>
      Number(y.soon) - Number(x.soon) ||
      (x.soon && y.soon ? y.target - x.target : 0) ||
      (byNeeded ? x.needed - y.needed : 0) ||
      (y.playoffOdds ?? -1) - (x.playoffOdds ?? -1) ||
      (x.gamesAway ?? Infinity) - (y.gamesAway ?? Infinity) ||
      y.progress - x.progress ||
      x.personId.localeCompare(y.personId),
  );
}

/** The best of `marks`, the most recent of equal ones (as on the Hall of Fame). */
function bestOf<T extends { value: number; season: number }>(marks: readonly T[]): T | null {
  return [...marks].sort((a, b) => b.value - a.value || b.season - a.season)[0] ?? null;
}

/** Live regular-season paces within reach of a single-season record from a finished season. */
function seasonChases(history: LeagueHistory, ledger: readonly PersonGame[], active: readonly string[], state: SeasonState): RecordChase[] {
  const live = state.live;
  if (!live || !state.regularSeason) return [];
  const lines = buildSeasonLines(history, ledger);
  const played = (l: ManagerSeasonLine) => l.record.wins + l.record.losses + l.record.ties;
  const bigGames = new Map<string, number>();
  for (const g of ledger) {
    if (g.gameType === "regular" && g.points >= BIG_GAME_POINTS) bigGames.set(`${g.season}|${g.personId}`, (bigGames.get(`${g.season}|${g.personId}`) ?? 0) + 1);
  }
  const bigGamesOf = (l: ManagerSeasonLine) => bigGames.get(`${l.season}|${l.personId}`) ?? 0;
  // Each team plays every regular-season week, so the season's length is its weeks.
  const seasonGames = live.playoffStartWeek ? live.playoffStartWeek - live.startWeek : null;

  /** `perGame` is one game's contribution: a running total of it, or for a rate its running average. */
  const stats: { kind: RecordChaseKind; value: (l: ManagerSeasonLine) => number; rate: boolean; perGame: (g: PersonGame) => number }[] = [
    { kind: "seasonPointsPerGame", value: (l) => l.pointsFor / played(l), rate: true, perGame: (g) => g.points },
    { kind: "seasonWins", value: (l) => l.record.wins, rate: false, perGame: (g) => (g.result === "W" ? 1 : 0) },
    { kind: "seasonBigGames", value: bigGamesOf, rate: false, perGame: (g) => (g.points >= BIG_GAME_POINTS ? 1 : 0) },
  ];
  const regularGames = new Map<string, PersonGame[]>();
  for (const g of ledger) {
    if (g.gameType === "regular") regularGames.set(`${g.season}|${g.personId}`, [...(regularGames.get(`${g.season}|${g.personId}`) ?? []), g]);
  }
  /** The running mark after each of a manager's regular-season games that season. */
  const runOf = (season: number, personId: string, perGame: (g: PersonGame) => number, rate: boolean): number[] => {
    let total = 0;
    return (regularGames.get(`${season}|${personId}`) ?? []).map((g, i) => {
      total += perGame(g);
      return round2(rate ? total / (i + 1) : total);
    });
  };
  const finished = lines.filter((l) => l.complete && played(l) > 0);
  const chasers = lines.filter((l) => l.season === live.season && active.includes(l.personId) && played(l) >= MIN_PACE_GAMES);

  return stats.flatMap(({ kind, value, rate, perGame }) => {
    const marks = finished.map((l) => ({ value: value(l), season: l.season, personId: l.personId }));
    const record = bestOf(marks);
    if (!record || record.value <= 0) return [];
    const recordHolders = marks.filter((m) => m.value === record.value).length;
    return chasers.flatMap((line): RecordChase[] => {
      const current = value(line);
      if (!rate && seasonGames === null) return [];
      const left = rate ? 0 : seasonGames! - played(line);
      const pace = rate ? current : (current / played(line)) * seasonGames!;
      // A count that can't reach the record in the games left isn't a chase, whatever its pace.
      if (pace < RECORD_PACE_SHARE * record.value || (!rate && current + left < record.value)) return [];
      return [
        {
          kind,
          personId: line.personId,
          current: round2(current),
          pace: rate ? null : round2(pace),
          race: {
            chaser: runOf(line.season, line.personId, perGame, rate),
            record: runOf(record.season, record.personId, perGame, rate),
            seasonGames: seasonGames ?? played(line),
          },
          record: round2(record.value),
          holderId: record.personId,
          recordHolders,
          holderSeason: record.season,
          seasonRecord: line.record,
          gamesAway: null,
          banked: Math.min(1, current / record.value),
          progress: Math.min(1, pace / record.value),
        },
      ];
    });
  });
}

/** Active win streaks within a couple of games of the longest ever. */
function streakChases(history: LeagueHistory, games: readonly PersonGame[], active: readonly string[], state: SeasonState): RecordChase[] {
  const live = state.live;
  if (!live) return [];
  const gamesOf = new Map<string, PersonGame[]>();
  for (const g of games) gamesOf.set(g.personId, [...(gamesOf.get(g.personId) ?? []), g]);
  const longest = new Map(history.people.map((p) => [p.id, summarizeStreaks(gamesOf.get(p.id) ?? [])]));

  return active.flatMap((id): RecordChase[] => {
    const current = longest.get(id)?.current;
    if (!current || current.result !== "W" || current.season !== live.season || !state.playingOn.has(id)) return [];
    // The record to chase is every other run, including the chaser's own earlier ones, but not this one.
    const earlier = summarizeStreaks((gamesOf.get(id) ?? []).slice(0, -current.length)).longestWin;
    const candidates: (Streak & { personId: string })[] = [];
    for (const [personId, summary] of longest) {
      const run = personId === id ? earlier : summary.longestWin;
      if (run) candidates.push({ ...run, personId });
    }
    const record = candidates.sort(compareStreaks)[0];
    if (!record || current.length < record.length - STREAK_CHASE_GAP) return [];
    return [
      {
        kind: "winStreak",
        personId: id,
        current: current.length,
        pace: null,
        race: null,
        record: record.length,
        holderId: record.personId,
        recordHolders: candidates.filter((c) => c.length === record.length).length,
        holderSeason: record.season,
        seasonRecord: null,
        gamesAway: null,
        banked: Math.min(1, current.length / record.length),
        progress: Math.min(1, current.length / record.length),
      },
    ];
  });
}

/** Active managers expected to pass a career leader within the window. */
function careerChases(tallies: ReadonlyMap<string, Tally>, active: readonly string[], state: SeasonState): RecordChase[] {
  return CAREER_CHASES.flatMap(({ kind, rule: ruleKind }) => {
    const rule = ruleFor(ruleKind);
    const board = [...tallies].map(([personId, t]) => ({ personId, value: rule.value(t) })).sort((a, b) => b.value - a.value);
    const leader = board[0];
    if (!leader || leader.value <= 0) return [];
    return active.flatMap((id): RecordChase[] => {
      const tally = tallies.get(id);
      if (!tally || id === leader.personId) return [];
      // In a live season it has to be able to happen this season; in the offseason, next season's games count.
      const canAdd = state.live === null || (state.playingOn.has(id) && (rule.opportunity !== "blowoutWeeks" || inBlowoutWeeks(state)));
      const current = rule.value(tally);
      const needed = leader.value - current + (rule.countable ? 1 : 0.01);
      const gamesAway = gamesAt(needed, current, rule.chances(tally));
      if (!canAdd || gamesAway === null || gamesAway > MILESTONE_WINDOW_GAMES) return [];
      return [
        {
          kind,
          personId: id,
          current,
          pace: null,
          race: null,
          record: leader.value,
          holderId: leader.personId,
          recordHolders: board.filter((b) => b.value === leader.value).length,
          holderSeason: null,
          seasonRecord: null,
          gamesAway,
          banked: Math.min(1, current / leader.value),
          progress: Math.min(1, current / leader.value),
        },
      ];
    });
  });
}
